import type { ObjectArea, ObjectAreaId, ObjectAreaPath } from './objectAreas';
import type { AreaStatusInput } from './objectAreaStatus';

export interface GuestItem {
  id: 'anfahrt' | 'parken' | 'hausordnung' | 'zugang' | 'nachrichten' | 'gastportal';
  label: string;
  detail: string;
  tone: 'ok' | 'open' | 'neutral';
  to: ObjectAreaPath;
  /** Nächster Schritt, wenn etwas fehlt. */
  action?: string;
}

const plural = (value: number, one: string, many: string) => `${value} ${value === 1 ? one : many}`;

/**
 * Was Gäste dieses Objekts sehen und bekommen — je Bereich eine Zeile mit ihrem Stand. Einen
 * nächsten Schritt nennt eine Zeile nur dem, der ihn auch gehen darf.
 */
export function guestItems(
  input: AreaStatusInput,
  areas: ObjectArea[],
  canEdit: boolean,
): GuestItem[] {
  const visible = new Set<ObjectAreaId>(areas.map((area) => area.id));
  const { group } = input;
  const items: GuestItem[] = [];
  const next = (action: string) => (canEdit ? { action } : {});

  if (visible.has('anfahrt') && input.arrivalSteps !== undefined) {
    items.push(
      input.arrivalSteps > 0
        ? item(
            'anfahrt',
            'Anfahrt',
            'ok',
            plural(input.arrivalSteps, 'Schritt', 'Schritte') + ' bis zur Wohnungstür',
          )
        : { ...item('anfahrt', 'Anfahrt', 'open', 'Noch keine Schritte'), ...next('Anlegen') },
    );
  }
  // Ohne Stellplätze entscheiden erst die Alternativen, ob es „keine" heißt.
  const { parkingSpots, parkingAlternatives } = input;
  if (
    visible.has('parken') &&
    parkingSpots !== undefined &&
    (parkingSpots > 0 || parkingAlternatives !== undefined)
  ) {
    const parts = [
      parkingSpots > 0 ? plural(parkingSpots, 'Stellplatz', 'Stellplätze') : null,
      parkingAlternatives ? plural(parkingAlternatives, 'Alternative', 'Alternativen') : null,
    ].filter(Boolean);
    items.push(
      parts.length > 0
        ? item('parken', 'Parken', 'ok', parts.join(' · '))
        : item('parken', 'Parken', 'neutral', 'Keine Stellplätze · optional'),
    );
  }
  const de = !!group.houseRulesDe?.trim();
  const en = !!group.houseRulesEn?.trim();
  items.push(
    de || en
      ? item(
          'hausordnung',
          'Hausordnung',
          'ok',
          de && en ? 'Deutsch und Englisch' : de ? 'Nur Deutsch' : 'Nur Englisch',
        )
      : {
          ...item('hausordnung', 'Hausordnung', 'open', 'Noch nicht hinterlegt'),
          ...next('Hinterlegen'),
        },
  );
  items.push(
    group.hasBuildingAccessCode
      ? item('zugang', 'Haustürcode', 'ok', 'Hinterlegt · erst 12 h vor Anreise sichtbar')
      : item('zugang', 'Haustürcode', 'neutral', 'Keiner hinterlegt · optional'),
  );
  if (visible.has('nachrichten') && input.catalog) {
    const messages = input.catalog.messages;
    const active = messages.filter((message) => message.isEnabled).length;
    items.push(item('nachrichten', 'Nachrichten', 'ok', `${active} von ${messages.length} aktiv`));
  }
  if (visible.has('gastportal') && input.branding) {
    const name = input.branding.brandName?.trim();
    items.push(
      name || input.branding.hasLogo
        ? item('gastportal', 'Gastportal', 'ok', name ? `Eigene Marke „${name}“` : 'Eigenes Logo')
        : item('gastportal', 'Gastportal', 'neutral', 'StayOn-Standard'),
    );
  }
  return items;
}

const TARGETS: Record<GuestItem['id'], ObjectAreaPath> = {
  anfahrt: '/property-groups/$id/anfahrt',
  parken: '/property-groups/$id/parken',
  hausordnung: '/property-groups/$id/hausordnung',
  zugang: '/property-groups/$id/stammdaten',
  nachrichten: '/property-groups/$id/nachrichten',
  gastportal: '/property-groups/$id/gastportal',
};

function item(id: GuestItem['id'], label: string, tone: GuestItem['tone'], detail: string) {
  return { id, label, tone, detail, to: TARGETS[id] };
}
